import { Head, useForm, Link, router } from "@inertiajs/react"
import { ArrowLeft, Camera, X } from "lucide-react"
import { useState, useRef } from "react"

import AppLayout from "@/layouts/app-layout"
import { samplesPath, newSamplePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Combobox } from "@/components/ui/combobox"

const processOptions = [
  { value: "natural", label: "Natural" },
  { value: "washed", label: "Lavado" },
  { value: "honey", label: "Honey" },
]

const varietalOptions = [
  { value: "typica", label: "Typica" },
  { value: "caturra", label: "Caturra" },
  { value: "catuai", label: "Catuaí" },
  { value: "gesha", label: "Gesha" },
  { value: "bourbon_amarillo", label: "Bourbon Amarillo" },
  { value: "bourbon_rosado", label: "Bourbon Rosado" },
  { value: "solok", label: "Solok" },
]

interface Tasting {
  id: number
  title: string
}

interface Props {
  tastings: Tasting[]
  product_type: string
  cupping_format: string
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Muestras",
    href: samplesPath(),
  },
  {
    title: "Nueva Muestra",
    href: newSamplePath(),
  },
]

const productTypeLabels: Record<string, string> = {
  coffee: "Café",
  wine: "Vino",
}

export default function NewStandaloneSample({ product_type, tastings }: Props) {
  const [picturePreview, setPicturePreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data, setData, processing, errors } = useForm<{
    name: string
    picture: File | null
    description: string
    origin: string
    land: string
    producer: string
    process: string
    variety: string[]
    tasting_id: string
  }>({
    name: "",
    picture: null,
    description: "",
    origin: "",
    land: "",
    producer: "",
    process: "",
    variety: [],
    tasting_id: "",
  })

  const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setData("picture", file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setPicturePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const removePicture = () => {
    setData("picture", null)
    setPicturePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const formData = new FormData()
    formData.append("sample[name]", data.name)
    formData.append("sample[description]", data.description)
    formData.append("sample[origin]", data.origin)
    formData.append("sample[land]", data.land)
    formData.append("sample[producer]", data.producer)
    formData.append("sample[process]", data.process)
    formData.append("sample[variety]", data.variety.join(", "))
    if (data.tasting_id) {
      formData.append("sample[tasting_id]", data.tasting_id)
    }
    if (data.picture) {
      formData.append("sample[picture]", data.picture)
    }
    router.post(samplesPath(), formData as any)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Nueva Muestra" />

      <div className="flex h-full flex-1 flex-col gap-4 p-4">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <Link href={samplesPath()}>
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle>Nueva Muestra</CardTitle>
                  <span className="bg-primary/10 text-primary inline-flex items-center rounded-full px-3 py-1 text-xs font-medium">
                    {productTypeLabels[product_type] ?? product_type}
                  </span>
                </div>
                <CardDescription>
                  Crea una nueva muestra de{" "}
                  {(
                    productTypeLabels[product_type] ??
                    product_type ??
                    "producto"
                  ).toLowerCase()}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre *</Label>
                  <Input
                    id="name"
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                    placeholder="Ej: Muestra A"
                    required
                  />
                  {errors.name && (
                    <p className="text-destructive text-sm">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tasting_id">Cata</Label>
                  <Select
                    value={data.tasting_id}
                    onValueChange={(value) => setData("tasting_id", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona una cata (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {tastings.map((tasting) => (
                        <SelectItem
                          key={tasting.id}
                          value={tasting.id.toString()}
                        >
                          {tasting.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.tasting_id && (
                    <p className="text-destructive text-sm">
                      {errors.tasting_id}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Imagen</Label>
                  <div className="flex items-center gap-4">
                    {picturePreview ? (
                      <div className="relative">
                        <img
                          src={picturePreview}
                          alt="Preview"
                          className="h-24 w-24 rounded-lg border object-cover"
                        />
                        <button
                          type="button"
                          onClick={removePicture}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90 absolute -top-2 -right-2 rounded-full p-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="picture"
                        className="border-muted-foreground/25 hover:border-primary/50 flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors"
                      >
                        <Camera className="text-muted-foreground h-8 w-8" />
                        <span className="text-muted-foreground mt-1 text-xs">
                          Subir foto
                        </span>
                      </label>
                    )}
                    <input
                      ref={fileInputRef}
                      id="picture"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePictureChange}
                      className="hidden"
                    />
                  </div>
                  {errors.picture && (
                    <p className="text-destructive text-sm">{errors.picture}</p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">Descripción</Label>
                  <Input
                    id="description"
                    value={data.description}
                    onChange={(e) => setData("description", e.target.value)}
                    placeholder="Descripción de la muestra"
                  />
                  {errors.description && (
                    <p className="text-destructive text-sm">
                      {errors.description}
                    </p>
                  )}
                </div>

                {product_type === "coffee" && (
                  <div className="space-y-3 md:col-span-2">
                    <div>
                      <h3 className="text-foreground text-sm font-semibold">
                        Origen
                      </h3>
                      <p className="text-muted-foreground text-xs">
                        Procedencia geográfica de la muestra
                      </p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="origin">País</Label>
                        <Input
                          id="origin"
                          value={data.origin}
                          onChange={(e) => setData("origin", e.target.value)}
                          placeholder="Ej: Colombia"
                        />
                        {errors.origin && (
                          <p className="text-destructive text-sm">
                            {errors.origin}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="land">Ubicación</Label>
                        <Input
                          id="land"
                          value={data.land}
                          onChange={(e) => setData("land", e.target.value)}
                          placeholder="Ej: Huila, Finca El Paraíso"
                        />
                        {errors.land && (
                          <p className="text-destructive text-sm">
                            {errors.land}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="producer">Productor</Label>
                  <Input
                    id="producer"
                    value={data.producer}
                    onChange={(e) => setData("producer", e.target.value)}
                    placeholder="Nombre del productor"
                  />
                  {errors.producer && (
                    <p className="text-destructive text-sm">
                      {errors.producer}
                    </p>
                  )}
                </div>

                {product_type === "coffee" && (
                  <div className="space-y-2">
                    <Label htmlFor="process">Proceso</Label>
                    <Select
                      value={data.process}
                      onValueChange={(value) => setData("process", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona un proceso" />
                      </SelectTrigger>
                      <SelectContent>
                        {processOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.process && (
                      <p className="text-destructive text-sm">
                        {errors.process}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="variety">Varietal</Label>
                  <div className="space-y-2">
                    {data.variety.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {data.variety.map((v) => {
                          const option = varietalOptions.find(
                            (opt) => opt.value === v,
                          )
                          return (
                            <div
                              key={v}
                              className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium"
                            >
                              <span>{option?.label || v}</span>
                              <button
                                type="button"
                                onClick={() =>
                                  setData(
                                    "variety",
                                    data.variety.filter((val) => val !== v),
                                  )
                                }
                                className="hover:text-primary/80"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                    <Combobox
                      options={varietalOptions.filter(
                        (opt) => !data.variety.includes(opt.value),
                      )}
                      value=""
                      onChange={(value) => {
                        if (value && !data.variety.includes(value)) {
                          setData("variety", [...data.variety, value])
                        }
                      }}
                      placeholder="Agregar varietal..."
                    />
                  </div>
                  {errors.variety && (
                    <p className="text-destructive text-sm">{errors.variety}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={processing}>
                  Crear Muestra
                </Button>
                <Link href={samplesPath()}>
                  <Button type="button" variant="outline">
                    Cancelar
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
